// @flow

import React from 'react';
import {Link} from 'react-router-dom';
import {Hidden, Icon, Typography, makeStyles} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import {usePage} from '../../../page/utils';

import CustomButton from '../../../../elements/CustomButton/CustomButton';
import {colors} from '../../../../assets/styles/colors';
import DraggableInputs from '../components/DraggableContent/DraggableInputs';
import {useTypedSelector} from '../../../../store';

import SnackBar from '../../../../elements/SnackBar/SnackBar';
import {useActions} from '../../../../utils/globalStore';
import LoadingSpinner from '../../../../elements/LoadingSpinner/LoadingSpinner';

import {actions as CheckoutActions} from '../duck';
import {DISABLED_DEFAULT_CHECKOUT_INPUTS} from '../constants/allowed-inputs';

import {useIsFeatureInTier} from '../../../../utils/component';
import PremiumFeatureBadge from '../../../../elements/PremiumFeatureBadge/PremiumFeatureBadge';
import {useMinimalTierContext} from '../../../../context/MinimalProvider';
import CustomCheckoutDisabledModal from '../components/Modals/CustomCheckoutDisabledModal';

import type {Theme} from '@material-ui/core';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    marginTop: theme.spacing(2),
    // padding: theme.spacing(2, 0),
  },
  header: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  flex: {
    display: 'flex',
  },
  cardWhite: {
    padding: theme.spacing(2),
    background: 'white',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    // backgroundColor: 'white',
    borderRadius: '12px',
    overflow: 'hidden',
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 600,
  },
  title: {
    color: colors.grey900,
    fontSize: 14,
    fontWeight: 500,
  },
  description: {
    color: colors.grey800,
    fontSize: 14,
  },
  addNewButton: {
    background: '#F8F9FB',
    border: '1px dashed #D4D4DD',
    borderRadius: '8px',
    padding: theme.spacing(1, 2),
    display: 'flex',
    justifyContent: 'center',
  },
  addNewButtonDisabled: {
    opacity: '0.5',
  },
  wrapperAddBtn: {
    display: 'flex',
    alignItems: 'center',
  },
  addIcon: {
    borderRadius: '50%',
    marginRight: '1rem',
  },
  btnAddText: {
    fontSize: 14,
    fontWeight: 500,
  },
}));

type Props = {};

export default usePage<Props>(CheckoutConfig, 'Checkout Config');

function CheckoutConfig(props: Props) {
  const classes = useStyles();

  const hasCustomCheckoutFeature = useIsFeatureInTier('CUSTOM_ORDER_CHECKOUT');

  const {
    handleOpenRestriction,
    openRestriction,
    handleCloseRestriction,
  } = useMinimalTierContext();

  const isSaving = useTypedSelector(state => state.salesProcessConfig.isSaving);
  const resetState = useActions(CheckoutActions.resetCheckoutPage);

  // const saveSuccess = useTypedSelector(
  //   state => state.salesProcessConfig.saveCheckoutInputSuccess
  // );
  const saveError = useTypedSelector(
    state => state.salesProcessConfig.saveInputError
  );

  const checkoutInputs = useTypedSelector(
    state => state.salesProcessConfig.salesProcessInformation
  );

  const onlyCustomInputs = (checkoutInputs ?? []).filter(
    v => !DISABLED_DEFAULT_CHECKOUT_INPUTS.includes(v.name)
  );

  const addButton = (
    <CustomButton
      classes={{disabled: classes.addNewButtonDisabled}}
      className={classes.addNewButton}
      component={Link}
      to="/configure/sales-process/checkout/new"
      onClick={resetState}
      disabled={!hasCustomCheckoutFeature}
    >
      <div className={classes.wrapperAddBtn}>
        <Icon size="small" color="primary" className={classes.addIcon}>
          <AddIcon />
        </Icon>
        <Typography color="primary" className={classes.btnAddText}>
          Agregar campo
        </Typography>
      </div>
    </CustomButton>
  );

  const wrapperStyle = {
    opacity: !hasCustomCheckoutFeature ? '0.5' : undefined,
    // pointerEvents: !hasCustomCheckoutFeature ? 'none' : undefined,
  };

  return (
    <div
      className={`${classes.root} ${classes.cardWhite}`}
      onClick={handleOpenRestriction}
    >
      <CustomCheckoutDisabledModal
        open={openRestriction}
        handleClose={e => {
          if (e) e.stopPropagation();
          handleCloseRestriction();
        }}
      />

      {/* {saveSuccess && (
        <SnackBar mode="success" message="Guardado correctamente" />
      )} */}
      {!!saveError && <SnackBar mode="error" message={saveError} />}
      {isSaving && <LoadingSpinner />}
      <header className={classes.header}>
        <Typography className={classes.sectionTitle}>
          Campos Adicionales{' '}
          {!hasCustomCheckoutFeature && <PremiumFeatureBadge />}
        </Typography>
        <Hidden xsDown>{addButton}</Hidden>
      </header>

      <div>
        <Typography className={classes.description}>
          Crea campos adicionales para que tu cliente pueda colocar la
          información del pedido. Úsalos para pedir data extra como: fecha de
          entrega, horarios o comentarios.
        </Typography>

        <Hidden mdUp>
          <div style={{marginTop: '1rem'}}>{addButton}</div>
        </Hidden>
        <div
          style={{
            marginTop: '16px',
            ...wrapperStyle,
          }}
        >
          {checkoutInputs && <DraggableInputs inputs={onlyCustomInputs} />}
        </div>
      </div>
    </div>
  );
}
